/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { ColumnCommonProps } from '@g17eco/types/survey-question-list';
import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { hasOverriddenUtrvConfig, iconMap, UtrvConfigCode, UtrvConfigValue } from '@features/question-configuration';
import { Column } from '@g17eco/molecules/tracking-list';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

interface ColumnFlagsProps extends ColumnCommonProps {
  verificationRequired: boolean;
  evidenceRequired: boolean;
  noteRequired?: boolean;
  rootInitiativeUtrMap?: Map<string, InitiativeUniversalTracker>;
}

type UtrvConfigOptionalityCode = Exclude<UtrvConfigCode, 'isPrivate'>;

type FlagMap = Record<UtrvConfigOptionalityCode, { isRequiredAtUtrvLevel: boolean; isRequiredAtSurveyLevel: boolean }>;

const getIcon = (isRequired: boolean, field: UtrvConfigOptionalityCode) => {
  return {
    className: `${isRequired ? iconMap[field].requiredIcon : iconMap[field].optionalIcon} mx-1`,
    tooltip: `${iconMap[field].label} ${isRequired ? 'required' : 'optional'}`,
  };
};

const getIcons = ({
  flagMap,
  initiativeUtr,
}: {
  flagMap: FlagMap;
  initiativeUtr: InitiativeUniversalTracker | undefined;
}) => {
  const icons: { className: string; tooltip: string }[] = [];

  Object.entries(flagMap).forEach(([flag, { isRequiredAtUtrvLevel, isRequiredAtSurveyLevel }]) => {
    const field = flag as UtrvConfigOptionalityCode;
    if (hasOverriddenUtrvConfig({ initiativeUtr, field })) {
      const isRequired = initiativeUtr?.utrvConfig?.[field] === UtrvConfigValue.Required;
      icons.push(getIcon(isRequired, field));
      return;
    }
    if (isRequiredAtUtrvLevel !== isRequiredAtSurveyLevel) {
      icons.push(getIcon(isRequiredAtUtrvLevel, field));
    }
  });

  return icons;
};

export const ColumnFlags = (props: ColumnFlagsProps) => {
  const { question, verificationRequired, evidenceRequired, noteRequired, rootInitiativeUtrMap } = props;
  const initiativeUtr = rootInitiativeUtrMap?.get(question.universalTracker.getId());

  const flagMap: FlagMap = {
    evidenceRequired: {
      isRequiredAtUtrvLevel: !!question?.utrv?.evidenceRequired,
      isRequiredAtSurveyLevel: evidenceRequired,
    },
    verificationRequired: {
      isRequiredAtUtrvLevel: !!question?.utrv?.verificationRequired,
      isRequiredAtSurveyLevel: verificationRequired,
    },
    noteRequired: {
      isRequiredAtUtrvLevel: !!question?.utrv?.noteRequired,
      isRequiredAtSurveyLevel: !!noteRequired,
    },
  };

  const icons = getIcons({ flagMap, initiativeUtr });

  if (icons.length === 0) {
    return null;
  }

  return (
    <Column className={'d-none d-md-inline-block flagsCol text-ThemeIconSecondary text-right pe-1'}>
      {icons.map((icon) => (
        <SimpleTooltip key={icon.className} text={icon.tooltip}>
          <i className={icon.className} />
        </SimpleTooltip>
      ))}
    </Column>
  );
};
